import {useFormik} from 'formik'
import {useCallback, useMemo} from 'react'
import {TextInput} from '../../../../components/inputs'
import {MetronicIconButton} from '../../../../components/inputs/MetronicIconButton'
import {SweetAlert} from '../../../../components/modals/SweetAlert'
import {VoucherModel} from '../../../../models/svc/VoucherModel'
import * as Yup from 'yup'
import {useAlerts} from '../../../../components/alerts/useAlerts'
import {HorizontalDivider} from '../../../../components/utils/HorizontalDivider'
import {Validators} from '../../../../utils/Validators'

interface WhatsAppVoucherModalProps {
  open: boolean
  setOpen: (value: boolean) => void
  data: VoucherModel[]
  onSubmit: (values: WhatsAppInitialVoucherValuesProps) => void
}

export interface WhatsAppInitialVoucherValuesProps {
  name: string
  mobile: string
}

export const WhatsAppVoucherModal = ({
  open,
  setOpen,
  data,
  onSubmit,
}: WhatsAppVoucherModalProps) => {
  const {pushError} = useAlerts()
  const initialValues = useMemo<WhatsAppInitialVoucherValuesProps>(() => {
    return {
      name: '',
      mobile: '',
    }
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: WHATAPP_SCHEMA,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        await onSubmit(values)
      } catch (err) {
        pushError(err)
      } finally {
        setSubmitting(false)
        setOpen(false)
      }
    },
  })

  const handleClose = useCallback(() => {
    setOpen(false)
    formik.resetForm()
  }, [formik, setOpen])

  const title = useMemo(() => {
    if (data.length > 1) return 'SHARE SELECTED VOUCHERS'
    return `SHARE VOUCHER: ${data[0].name}`
  }, [data])

  return (
    <SweetAlert
      containerClassName='mw-300px'
      customClass={{
        popup: 'bg-primary-cp-dark',
      }}
      open={open}
      onClose={handleClose}
      showConfirmButton={false}
    >
      <div className='d-flex'>
        <div className='flex-grow-1 text-start d-flex flex-column justify-content-center'>
          <p className='text-white mb-0 fw-bolder'>{title}</p>
        </div>
        <div>
          <MetronicIconButton
            type='button'
            className='btn'
            iconType='Navigation'
            iconName='Close'
            variant='text'
            size='md'
            color='white'
            onClick={handleClose}
          />
        </div>
      </div>
      <HorizontalDivider className='bg-dark' />
      <form className='form mt-5' onSubmit={formik.handleSubmit}>
        <TextInput
          type='name'
          placeholder='Enter Name'
          inputClassName='rounded-0'
          {...formik.getFieldProps('name')}
          errorMessage={formik.errors.name}
          isTouched={formik.touched.name}
        />

        <TextInput
          type='mobile'
          placeholder='+971555555555'
          inputClassName='rounded-0'
          {...formik.getFieldProps('mobile')}
          errorMessage={formik.errors.mobile}
          isTouched={formik.touched.mobile}
        />

        <button
          disabled={formik.isSubmitting}
          className='btn bg-body d-flex gap-2 m-auto mt-5 rounded-0 text-white w-75 justify-content-center'
          onClick={formik.submitForm}
        >
          Open whatsApp
          {formik.isSubmitting && (
            <span className='indicator-progress ml-2' style={{display: 'block'}}>
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </form>
    </SweetAlert>
  )
}

const WHATAPP_SCHEMA = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Name is required'),
  mobile: Yup.string()
    .min(10, 'Minimum 10 symbols')
    .required('Mobile is required')
    .test('Mobile must start with +', 'Mobile must start with +', (value) =>
      Validators.INTERNATIONAL_WHATSAPP_MOBILE_NUMBER_REGEX.test(value || '')
    ),
})
